import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import PaginatorComponent from "../../../common/paginator/paginatorComponent";
import IPortletSettings from "../../classes/PortletSettings";
import { IConfig } from "../../Config";
import DataService from "../../data/DataService";
import { ViewType } from "../../NewsroomView";
import Loader from "../Loader";
import Modal from "../Modal";
import RssFeedButton from "../RenderComponents/RssFeedButton";
import AddPostButton from "./components/AddPostButton";
import Filter from "./components/Filter";
import SettingsButton from "./components/SettingsButton";
import ShowArchivedButton from "./components/ShowArchivedButton";
import Grid from "./Grid";
import "./posts.css";
import Settings from "./Settings";
import Template from "./Template";

interface IProps {
    config: IConfig;
    currentPageNumber: number;
    currentPagePosts: any[];
    dataService: DataService;
    filterableCategories: any[];
    filterableTags: any[];
    isAdmin: boolean;
    isLoading: boolean;
    isViewingArchivedItems: boolean;
    onReinitialize: () => Promise<void>;
    onNewsDeleted: () => void;
    onSetIsLoading: (isLoading: boolean) => void;
    onSetIsViewingArchivedItems: (isViewingArchived: boolean) => void;
    onSetPageNumber: (newPageNumber: number) => void;
    onSetSearchText: (searchText: string) => void;
    onSetSelectedCategoriesAndTags: (categories: any[], tags: any[]) => void;
    onSwitchView: (view: ViewType, newsId?: string) => void;
    selectedCategories: any[];
    selectedTags: any[];
    settings: IPortletSettings;
    searchText: string;
    stylesheet: string;
    template: string;
    totalNumberOfPosts: number;
    writableFeedIds: string[];
}

const NumberOfPageLinks = 5;

const TextCurrentlyDisplayingPageText = "Currently displaying page";
const TextDisplayPageText = "Display page";
const TextDisplayPreviousPageOfResults = "Display previous page of results";
const TextDisplayPreviousSetOfPageLinks = "Display previous set of page links";
const TextDisplayNextPageOfResults = "Display next page of results";
const TextDisplayNextSetOfPageLinks = "Display next set of page links";
const TextNoNextPageOfResults = "No next page of results";
const TextNoNextSetOfPageLinks = "No next set of page links";
const TextNoPreviousPageOfResults = "No previous page of results";
const TextNoPreviousSetOfPageLinks = "No previous set of page links";
const TextOf = "of";
const TextOfResultsText = "of results";

const Posts = (props: IProps) => {
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    const debouncedLoadNews = useDebouncedCallback(async () => {
        props.onSetPageNumber(1);
    }, 800);

    const onDelete = async (newsId, isDeleted, isPermanent = false) => {
        props.onSetIsLoading(true);
        if (isDeleted) {
            await props.dataService.deleteNews(newsId, isPermanent);
        } else {
            const news = (await props.dataService.getNews(newsId)).data || {};
            if (news) {
                news.categories = _.map(news.categories, (o) => o.categoryId);
                news.tags = _.map(news.tags, (o) => o.tag);
                news.websites = _.map(news.websites, (o) => o.serverId);
                news.deleted = false;
                await props.dataService.updateNews(newsId, news);
            }
        }
        await props.onNewsDeleted();
    };

    const onSearch = (text: string) => {
        props.onSetSearchText(text);
        (async () => {
            await debouncedLoadNews();
        })();
    };

    useEffect(() => {
        const changeEvent = new CustomEvent("newsroomHasRendered", {
            detail: {
                portletInstanceId: props.config.portletInstanceId,
            },
        });

        if (!props.isLoading && props.currentPagePosts && props.currentPagePosts.length > 0) {
            window.document.dispatchEvent(changeEvent);
        }
    }, [props.currentPagePosts]);

    const renderFiltersForCategories = () => {
        if (props.filterableCategories.length === 0) {
            return (<span className="NR_filter_empty">There are currently no Categories.</span>);
        }

        return (
            <>
                {_.map(props.filterableCategories, (category, index) => (
                    <button
                        className={`CMS_tag ${_.indexOf(props.selectedCategories, category.categoryId) < 0 ? "CMS_tagInactive" : ""}`}
                        key={`category-${index}`}
                        style={{ marginRight: "4px" }}
                        onClick={async (e) => {
                            e.preventDefault();
                            const activeCategoriesClone = [...props.selectedCategories];
                            const i = _.indexOf(activeCategoriesClone, category.categoryId);
                            if (i < 0) {
                                activeCategoriesClone.push(category.categoryId);
                            } else {
                                activeCategoriesClone.splice(i, 1);
                            }
                            props.onSetSelectedCategoriesAndTags(activeCategoriesClone, props.selectedTags);
                        }}
                    >
                        {category.categoryName}
                    </button>
                ))}
            </>
        );
    };

    const renderFiltersForTags = () => {
        if (props.filterableTags.length === 0) {
            return (<span className="NR_filter_empty">There are currently no Tags.</span>);
        }

        return (
            <>
                {_.map(props.filterableTags, (tag, index) => (
                    <button
                        className={`CMS_tag ${_.indexOf(props.selectedTags, tag) < 0 ? "CMS_tagInactive" : ""}`}
                        key={`tag-${index}`}
                        style={{ marginRight: "4px" }}
                        onClick={async (e) => {
                            e.preventDefault();
                            const activeTagsClone = [...props.selectedTags];
                            const i = _.indexOf(props.selectedTags, tag);
                            if (i < 0) {
                                activeTagsClone.push(tag);
                            } else {
                                activeTagsClone.splice(i, 1);
                            }
                            props.onSetSelectedCategoriesAndTags(props.selectedCategories, activeTagsClone);
                        }}
                    >
                        {tag}
                    </button>
                ))}
            </>
        );
    };

    const onSettings = () => {
        setIsSettingsOpen(true);
    };

    const onShowArchived = () => {
        props.onSetPageNumber(1);
        props.onSetIsViewingArchivedItems(!props.isViewingArchivedItems);
    };

    const pagination = (
        <PaginatorComponent
            totalNumberOfRecords={props.totalNumberOfPosts}
            recordsPerPage={Math.max(props?.settings?.MaxPosts || 9, 1)}
            numberOfPageLinks={NumberOfPageLinks}
            currentPageNumber={props.currentPageNumber}

            text_currentlyDisplayingPage={TextCurrentlyDisplayingPageText}
            text_displayPage={TextDisplayPageText}
            text_displayPreviousPageOfResults={TextDisplayPreviousPageOfResults}
            text_displayPreviousSetOfPageLinks={TextDisplayPreviousSetOfPageLinks}
            text_displayNextPageOfResults={TextDisplayNextPageOfResults}
            text_displayNextSetOfPageLinks={TextDisplayNextSetOfPageLinks}

            text_noNextPageOfResults={TextNoNextPageOfResults}
            text_noNextSetOfPageLinks={TextNoNextSetOfPageLinks}
            text_noPreviousPageOfResults={TextNoPreviousPageOfResults}
            text_noPreviousSetOfPageLinks={TextNoPreviousSetOfPageLinks}

            text_of={TextOf}
            text_ofResultsText={TextOfResultsText}

            onChangePage={props.onSetPageNumber}
        />
    );

    return (
        <Loader isLoading={props.isLoading || _.isEmpty(props.settings.LayoutStyle)}>
            {props.settings.LayoutStyle === "template" ? (
                <Template
                    allCategories={props.filterableCategories}
                    allTags={props.filterableTags}
                    isAdmin={props.isAdmin}
                    posts={props.currentPagePosts}
                    page={props.currentPageNumber}
                    config={props.config}
                    template={props.template}
                    stylesheet={props.stylesheet}
                    activeCategories={props.selectedCategories}
                    activeTags={props.selectedTags}
                    isViewingArchivedItems={props.isViewingArchivedItems}
                    pagination={pagination}
                    showAuthor={props.settings.ShowAuthor}
                    searchText={props.searchText}
                    updateSelectedCategoriesAndTags={props.onSetSelectedCategoriesAndTags}
                    onSettings={onSettings}
                    onShowArchived={onShowArchived}
                    onDelete={onDelete}
                    onSearch={onSearch}
                    onSwitchView={props.onSwitchView}
                    renderFiltersForCategories={renderFiltersForCategories}
                    renderFiltersForTags={renderFiltersForTags}
                    writableFeedIds={props.writableFeedIds}
                />
            ) : (
                <div style={{ maxWidth: "1200px", margin: "50px auto" }}>
                    <div className="CMS_element NR-wrapper">
                        <div className="CMS_contentArea">
                            <div className="CMS_e_row">
                                {props.settings.LayoutStyle !== "template"
                                    ? (
                                        <RssFeedButton config={props.config} />
                                    ) : null}
                                <div style={{ float: "right" }}>
                                    <Filter
                                        renderFiltersForCategories={renderFiltersForCategories}
                                        renderFiltersForTags={renderFiltersForTags}
                                    />
                                </div>
                            </div>
                            {props.isAdmin ? <div className="CMS_e_row">
                                <div className="CMS_buttonGroup">
                                    <AddPostButton
                                        isAdmin={props.isAdmin}
                                        config={props.config}
                                        onCreateNew={() => props.onSwitchView(ViewType.EditForm)}
                                    />&nbsp;
                                    <SettingsButton
                                        isAdmin={props.isAdmin}
                                        onClick={onSettings}
                                    />&nbsp;
                                    <ShowArchivedButton
                                        isAdmin={props.isAdmin}
                                        includeDeleted={props.isViewingArchivedItems}
                                        onClick={onShowArchived}
                                    />
                                </div>
                            </div> : null}
                            <div className="CMS_e_row">
                                <Grid
                                    posts={props.currentPagePosts}
                                    config={props.config}
                                    activeCategories={props.selectedCategories}
                                    activeTags={props.selectedTags}
                                    page={props.currentPageNumber}
                                    onDelete={onDelete}
                                    onSwitchView={props.onSwitchView}
                                    headingLevel={props.settings.HeadingLevel}
                                    isAdmin={props.isAdmin}
                                    isViewingArchivedItems={props.isViewingArchivedItems}
                                    layoutStyle={props.settings.LayoutStyle}
                                    showAuthor={props.settings.ShowAuthor}
                                    writableFeedIds={props.writableFeedIds}
                                />
                            </div>
                            <div className="CMS_e_row">
                                {pagination}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Modal isOpen={isSettingsOpen} isClosable={false}>
                <Settings
                    config={props.config}
                    onCancel={() => setIsSettingsOpen(false)}
                    onUpdate={async () => {
                        setIsSettingsOpen(false);
                        await props.onReinitialize();
                    }}
                />
            </Modal>
        </Loader>
    );
};

export default Posts;
